/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useRef, type KeyboardEvent, type ReactNode } from 'react';

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
}

export interface SegmentedProps<T extends string> {
  /** The group's accessible name ("Share of"). */
  label: string;
  options: Array<SegmentedOption<T>>;
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
  /** Shows the choice without letting it change (a read-only role). */
  disabled?: boolean;
  /** `is-changed` gives an unsaved choice the amber edge a changed input has. */
  className?: string;
}

/** A small set of mutually exclusive views (Cost / Requests). A radio group: arrow keys move between options. */
export function Segmented<T extends string>({ label, options, value, onChange, size = 'sm', disabled = false, className }: SegmentedProps<T>) {
  const group = useRef<HTMLDivElement>(null);
  const onKey = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const next = options[(index + step + options.length) % options.length];
    if (!next) return;
    onChange(next.value);
    const buttons = group.current?.querySelectorAll<HTMLButtonElement>('button');
    buttons?.[(index + step + options.length) % options.length]?.focus();
  };
  return (
    <div ref={group} role="radiogroup" aria-label={label} aria-disabled={disabled || undefined} className={['ui-seg', size === 'md' ? 'is-md' : '', className ?? ''].filter(Boolean).join(' ')}>
      {options.map((option, index) => {
        const on = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            disabled={disabled}
            className={on ? 'is-on' : undefined}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => onKey(event, index)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
